// @flow
import * as React from 'react';

import { connect, useDispatch } from 'react-redux';

import { Link } from 'react-router-dom';
// eslint-disable-next-line import/no-extraneous-dependencies
import { createFilter } from 'react-select';

import { useEntityPermissions } from '@gandi/acl';
import { selectIsSamlUser } from '@gandi/authentication';
import {
  selectIsLoadedCartOwnerList as selectLoadedCartOwners,
  selectIsFailureCartOwnerList as selectFailureCartOwners,
  selectIsFetchingCartOwnerList as selectFetchingCartOwners,
  fetchCartOwnerList as fetchCartOwners,
  selectCartUUID as selectCartId,
  selectHasOwnerOnly,
} from '@gandi/cart';
import { CountryName } from '@gandi/react-places';
import {
  selectPersonalId,
  selectCurrentId,
  type Organization,
} from '@gandi/redux-sharing';
import { useTranslator, type TranslatorProps } from '@gandi/translate';
import { Base, Text, Badge, Svg } from '@gandi/ui';
import { Custom } from '@gandi/ui-select';

import { selectHasMissingOrgInfo } from '../../../modules/organization/selectors';
import { ORGA_WRITE } from '../../../modules/permissions';
import type {
  ComponentWithPrefetch,
  FullState,
  PreFetchArgs,
} from '../../../types';
import { selectCartOwners } from '../modules/selectors';

import type { CartOwnerCandidate } from '../types';

import styles from './PayerSelect.scss';

type OwnProps = {
  onOwnerChange: (orgId: string) => void,
  frozen: boolean,
};

type StateProps = {
  ownerOnly: boolean,
  missingOrgInfo: boolean,
  loadedCartOwners: boolean,
  fetchingCartOwners: boolean,
  failureCartOwners: boolean,
  cartOwners: Array<CartOwnerCandidate>,
  cartId: string,
  personalId: string,
  currentId: string,
  isSamlUser: boolean,
};

export type Props = {
  ...OwnProps,
  ...StateProps,
};

const SVGStyle = { height: '1rem', width: 'auto' };
const textWrapStyle = { whiteSpace: 'normal' };

function stringify({ data }: any): string {
  return Object.keys(data)
    .filter(key => data[key] && typeof data[key] === 'string')
    .reduce((acc, key) => {
      // default case
      let substring = data[key];

      // Handle firstname / lastname semantic groups
      if (key === 'firstname') {
        substring = `${acc}${data.firstname} ${data.lastname}`;
      }
      if (key === 'lastname') {
        substring = `${acc}${data.lastname} ${data.firstname}`;
      }

      return `${acc}${substring} `;
    }, '');
}

function getOrgEdit(org: string, cart: string) {
  return `/cart/${cart}/configure/account/${org}?owner=cart`;
}

function getId(org: any): string {
  return org.id || '';
}

function getTypeLabel(
  __: TranslatorProps['__'],
  type: CartOwnerCandidate['type'],
) {
  if (type === 'individual') return __('Individual');
  if (type === 'company') return __('Company');
  if (type === 'association') return __('Association');
  if (type === 'publicbody') return __('Public Body');
  return __('Organization');
}

function getLegalName(
  __: TranslatorProps['__'],
  org: CartOwnerCandidate | Organization,
): string {
  let name =
    org.firstname && org.lastname ? `${org.firstname} ${org.lastname}` : '';
  if (org.type !== 'individual') {
    const legal = org.orgname || org.name || '';
    name = name ? `${legal} / ${name}` : legal;
  }
  return name;
}

export function getDisplayName(
  __: TranslatorProps['__'],
  org: CartOwnerCandidate | Organization,
): string {
  return org.name || org.orgname || '';
}

export function renderOption(
  __: TranslatorProps['__'],
  current: string,
  missingOrgInfo: boolean,
  org: CartOwnerCandidate | Organization,
): React.Node {
  const legalName = getLegalName(__, org);
  return (
    <Base padding="small" key={`owner-${org.id}`}>
      <Base tagName="p" margin="none">
        <Base flex flexAlignItemsCenter tagName="span">
          <Text weight="semibold">{getDisplayName(__, org)}</Text>
          {
            // $FlowFixMe only available when org: CartOwnerCandidate
            !!org.personal && <Badge className={styles.badge}>{__('Me')}</Badge>
          }
          {org.reseller && (
            <Svg
              className={styles.badge}
              title={__('Reseller')}
              name="badgeReseller"
              theme="anthracite"
              style={SVGStyle}
            />
          )}
          {org.corporate && (
            <Svg
              className={styles.badge}
              title={__('Corporate')}
              name="badgeCorporate"
              theme="anthracite"
              style={SVGStyle}
            />
          )}
        </Base>
        <Text color="light" size="small" transform="uppercase">
          {
            // $FlowFixMe[incompatible-call]
            getTypeLabel(__, org.type)
          }
        </Text>
        {legalName && (
          <>
            <br />
            <span>{legalName}</span>
          </>
        )}

        {org.email && (
          <>
            <br />
            <span>{org.email}</span>
          </>
        )}
        {org.country && org.city && org.streetaddr && (
          <>
            <br />
            <span style={textWrapStyle}>
              {`${org.streetaddr}${org.zip ? `, ${org.zip}` : ''}, ${
                org.city
              }, `}
              <CountryName country={org.country} />
            </span>
          </>
        )}
        {org.type === 'company' && org.id === current && !missingOrgInfo && (
          <>
            <br />
            <Text color={org.vat_number ? undefined : 'success'}>
              {org.vat_number
                ? __('VAT Number %(vat_number)s', {
                    vat_number: org.vat_number,
                  })
                : __('No VAT number')}
            </Text>
          </>
        )}
        {missingOrgInfo && org.id === current && (
          <>
            <br />
            <Text color="success">
              {__('Required information will be asked afterwards')}
            </Text>
          </>
        )}
      </Base>
    </Base>
  );
}

function onChange(
  callback: (id: string) => void,
  current: void | CartOwnerCandidate,
  update: (next: void | CartOwnerCandidate) => any,
  next_: any,
) {
  const next = Array.isArray(next_) ? next_[0] : next_;
  if (next !== current) {
    update(next);
    if (typeof callback === 'function') {
      callback(next.id);
    }
  }
}

export function PayerSelect({
  fetchingCartOwners,
  frozen,
  loadedCartOwners,
  failureCartOwners,
  cartOwners,
  cartId,
  ownerOnly,
  onOwnerChange,
  personalId,
  currentId,
  missingOrgInfo,
  isSamlUser,
}: Props): React.Node {
  const translator = useTranslator();
  const dispatch = useDispatch();
  const { __ } = translator;

  const [mounted, setMounted] = React.useState(false);

  const defaultOwner = cartOwners[0];
  const [owner, setOwner] = React.useState<void | CartOwnerCandidate>(
    defaultOwner,
  );

  const { hasPermissions, isLoadedPermissions, isFetchingPermissions } =
    useEntityPermissions(owner?.id || 'no-owner-yet');

  const shouldFetch =
    !fetchingCartOwners &&
    ((!loadedCartOwners && !failureCartOwners) || !mounted);

  React.useEffect(() => {
    if (shouldFetch) {
      dispatch(fetchCartOwners()).then(response => {
        // Check if the current organization was just created, set is as owner.
        if (owner?.id !== currentId) {
          const match = response.data.can_process.find(
            candidate => candidate.id === currentId,
          );
          if (match) {
            // $FlowFixMe[incompatible-call] Check in cart lib and type setOwner, fix wheeler-dealing
            setOwner(match);
          }
        }
      });
    }
    // Other dependencies don't matter here, we need to use the version of them
    // that's available via closure when the hook is run.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldFetch]);

  /* --------------- Force a re-render after hydration/mount. --------------- */
  // Required to mitigate a SSR/hydration bug in Safari
  React.useEffect(() => {
    if (!mounted) {
      setMounted(true);
    }
  }, [mounted, setMounted]);
  /* ------------------------------------------------------------------------ */

  const candidates = React.useMemo(
    () =>
      [
        cartOwners.find(candidate => candidate.id === personalId),
        ...cartOwners
          .filter(candidate => candidate.id !== personalId)
          .sort((a, b) =>
            getDisplayName(__, a).localeCompare(getDisplayName(__, b)),
          ),
      ].filter(Boolean),
    [cartOwners, personalId, __],
  );

  const [key, setKey] = React.useState(
    defaultOwner ? defaultOwner.id : 'not-loaded',
  );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleChange = React.useCallback(
    onChange.bind(null, onOwnerChange, owner, setOwner),
    [owner, setOwner],
  );

  React.useEffect(() => {
    let updated = defaultOwner;

    // List of cart owners changes between renders
    // while there already is an owner set.
    // This typically happens when user edits the currently selected org.
    // In such case, to avoid a de-sync between the current value and
    // the available options, we need to re-set the correct value and
    // re-mount the CustomSelect component with a correct defaultValue.
    if (!cartOwners.includes(owner)) {
      updated =
        cartOwners.find(candidate => candidate.id === owner?.id) ||
        defaultOwner;
    }

    setOwner(updated);
    setKey(String(Math.random()));
  }, [owner, defaultOwner, cartOwners]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const renderOpt = React.useCallback(
    renderOption.bind(null, __, currentId, missingOrgInfo),
    [__, currentId, missingOrgInfo],
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const filterOpt = React.useCallback(createFilter({ stringify }), []);

  const renderNoOpt = React.useCallback(
    () => __('Only organizations eligible for this order are listed'),
    [__],
  );

  return (
    <Base flex flexCol>
      <div className={styles.container}>
        <Custom
          key={key}
          id="payer-select-container"
          inputId="payer-select-input"
          instanceId="payer-select-instance"
          translator={translator}
          name="payer"
          isClearable={false}
          isDisabled={
            fetchingCartOwners || failureCartOwners || !mounted || frozen
          }
          defaultValue={defaultOwner}
          options={candidates}
          getOptionValue={getId}
          filterOption={filterOpt}
          // $FlowFixMe[incompatible-type]
          renderOption={renderOpt}
          onChange={handleChange}
          maxMenuHeight={500}
          noOptionsMessage={renderNoOpt}
        />
      </div>
      <span className={styles.linksContainer}>
        {owner &&
          isLoadedPermissions &&
          !isFetchingPermissions &&
          hasPermissions([ORGA_WRITE]) && (
            <>
              <Link
                to={getOrgEdit(owner.id, cartId)}
                className={styles.editLink}
              >
                {translator.__('Edit %(name)s', {
                  name: getDisplayName(__, owner),
                })}
              </Link>
              {!ownerOnly && !isSamlUser && <hr className={styles.separator} />}
            </>
          )}
        {!ownerOnly && !isSamlUser && (
          <Link to={`/cart/${cartId}/configure/account/new?owner=cart`}>
            {__('Create New')}
          </Link>
        )}
      </span>
    </Base>
  );
}

PayerSelect.preFetch = (
  args: PreFetchArgs<{}, any, any, FullState>,
): Promise<any> => {
  const { dispatch, getState } = args;
  const loadedCartOwners = selectLoadedCartOwners(getState());
  return Promise.allSettled(
    [
      CountryName.preFetch({ dispatch, getState }).catch(() => {}),
      !loadedCartOwners && dispatch(fetchCartOwners()),
    ].filter(Boolean),
  );
};

const mapStateToProps = (state: FullState): StateProps => {
  const fetchingCartOwners = selectFetchingCartOwners(state);
  const loadedCartOwners = selectLoadedCartOwners(state);
  const failureCartOwners = !!selectFailureCartOwners(state);
  const cartOwners = selectCartOwners(state);
  const cartId = selectCartId(state);
  const personalId = selectPersonalId(state);
  const currentId = selectCurrentId(state);

  const ownerOnly = selectHasOwnerOnly(state);
  const isSamlUser = selectIsSamlUser(state);
  const missingOrgInfo = !!selectHasMissingOrgInfo(state, currentId);

  return {
    ownerOnly,
    missingOrgInfo,
    loadedCartOwners,
    fetchingCartOwners,
    failureCartOwners,
    cartOwners,
    cartId,
    personalId,
    currentId,
    isSamlUser,
  };
};

// $FlowFixMe[signature-verification-failure]
// $FlowFixMe[underconstrained-implicit-instantiation]
export default (connect(mapStateToProps)(PayerSelect): ComponentWithPrefetch<
  OwnProps,
  typeof PayerSelect.preFetch,
>);
